import {
  AfterViewInit,
  Component,
  ElementRef,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  ViewChild,
} from '@angular/core';
import { isPlatformBrowser, isPlatformServer, Location } from '@angular/common';
import { Router } from '@angular/router';

import { Store } from '@ngrx/store';
import { combineLatest, Observable, of, Subscription } from 'rxjs';
import { distinctUntilChanged, map, startWith, take } from 'rxjs/operators';
import {
  DeepPartial,
  EnvConfig,
  ExtensionApi,
  GtmEvent,
  GtmEventData,
  GtmTrackElementData,
  Localizations,
  Locations,
  Parameters,
  parseLocalizations,
  renderToElement,
  renderToString,
  SelectionState,
  TravelClass,
  TravelDates,
  TravelType,
  unmount,
  Url,
  WidgetTab,
  WidgetTestVariant,
} from '@finnairoyj/fcom-booking-widget';

import { LanguageService } from '@fcom/ui-translate';
import {
  ConsentTextId,
  ElementTypes,
  GtmEvent as CommonGtmEvent,
  GtmEventData as CommonGtmEventData,
  RecommendationService,
  CountryShopService,
} from '@fcom/common';
import { GtmService } from '@fcom/common/gtm';
import { ConsentService, LoginService } from '@fcom/common/login';
import { GlobalBookingTripDates, TestVariant, TestVariantWithId, ContentType } from '@fcom/common/interfaces';
import { TripType } from '@fcom/core/constants';
import {
  globalBookingDates,
  globalBookingTravelClass,
  globalBookingTripType,
} from '@fcom/common/store/selectors/global-booking.selectors';
import { AppState, GlobalBookingTravelClass } from '@fcom/core';
import { CmsImageData } from '@fcom/core-api/interfaces';
import { loginToken, profile, profileOrNot } from '@fcom/core/selectors';
import { ConfigService } from '@fcom/core/services';
import { finShare, snapshot } from '@fcom/rx';
import { unsubscribe } from '@fcom/utils';
import { BookingLocationCodes } from '@fcom/booking';
import { getTravelTypeFromTripType } from '@fcom/common-booking/utils/common-booking.utils';
import { InstantSearchMonitoring } from '@fcom/booking-widget/interfaces/store.interface';
import { BookingWidgetActions } from '@fcom/booking-widget/store/actions';

/**
 * Standalone Angular wrapper for BookingWidget (Preact). Use this component when you want to use BookingWidget on
 * your page.
 */
@Component({
  selector: 'fin-booking-widget',
  templateUrl: './booking-widget.component.html',
})
// eslint-disable-next-line @angular-eslint/component-class-suffix
export class BookingWidgetComponentV1 implements OnInit, AfterViewInit, OnDestroy {
  /** Image to use as default background image before destination is selected. */
  @Input()
  campaignImage$: Observable<CmsImageData> = of(undefined);

  /** Can be set to override locations in the widget */
  @Input()
  locations$: Observable<BookingLocationCodes[]> = of([]);

  /** In embedded mode BookingWidget does not have a background image or any external spacers. */
  @Input()
  embedded = false;

  /** Disable persistence of the booking widget */
  @Input()
  enablePersistence = true;

  /** Enable award tab in widget */
  @Input()
  enableAward = false;

  /** Enable award tab in widget if logged in */
  @Input()
  enableAwardIfLoggedIn = false;

  /** Enable award tab in widget */
  @Input()
  enableAurinkomatkat = true;

  @Input()
  enablePromoCode = false;

  /** Enable the compact widget view */
  @Input()
  enableCompactWidget = false;

  @Input()
  enableAutoFocus = true;

  @Input()
  enableOpenJaw = false;

  /** Widget main Heading */
  @Input()
  shouldRenderMainHeading = true;

  /** Content type of where booking widget is injected, e.g. front_page, single_destination */
  @Input()
  contentType = ContentType.FRONT_PAGE;

  @Input()
  testVariant$: Observable<TestVariantWithId> = of(undefined);

  /**
   * If we want to give initial tab outside of this component.
   * Read from url if not given
   */
  @Input()
  initialTab: WidgetTab;

  @Input()
  shouldExpandWidget = false;

  @ViewChild('widgetRoot', { static: true })
  widgetRoot: ElementRef;
  widgetPlaceholder: string;

  private travelDates$: Observable<TravelDates>;
  private tripType$: Observable<TravelType>;
  private travelClass$: Observable<TravelClass>;
  private flow$: Observable<DeepPartial<SelectionState>>;
  private localizations$: Observable<Localizations>;
  private enablePersonalization$: Observable<boolean>;
  private envConfig: EnvConfig;
  private extensionApi: ExtensionApi;
  private subscription = new Subscription();
  private isInitialRender: boolean;

  constructor(
    private store$: Store<AppState>,
    private languageService: LanguageService,
    private consentService: ConsentService,
    private loginService: LoginService,
    private router: Router,
    private configService: ConfigService,
    gtmService: GtmService,
    recommendationService: RecommendationService,
    private location: Location,
    private countryShopService: CountryShopService,
    @Inject(PLATFORM_ID) private platform: Record<string, unknown>
  ) {
    const locationState = this.location.getState() as { navigationId: number };
    this.isInitialRender = locationState?.navigationId === 1;

    this.envConfig = {
      cmsUrl: this.configService.cfg.cmsUrl,
      instantSearchUrl: `${this.configService.cfg.instantSearchUrl}/api/instantsearch`,
      offersUrl: `${this.configService.cfg.offersUrl}/api/offerList`,
      airBoundsUrl: `${this.configService.cfg.offersUrl}/api/airBounds`,
      airCalendarUrl: `${this.configService.cfg.offersUrl}/api/airCalendar`,
      locationsUrl: this.configService.cfg.locationRouteCffUrl,
      recommendationUrl: this.configService.cfg.recommendationApiUrl,
      timeGeoUrl: this.configService.cfg.timeGeoUrl,
      configUrl: this.configService.cfg.configUrl,
      amApiUrl: this.configService.cfg.amApiUrl,
      amDeeplinkUrl: this.configService.cfg.amDeeplinkUrl,
    } as EnvConfig;
    this.extensionApi = {
      navigator: {
        navigateToBooking: ({ path }: Url, instantSearchMonitoring: InstantSearchMonitoring): Promise<boolean> => {
          this.store$.dispatch(BookingWidgetActions.setInstantSearchMonitoring({ instantSearchMonitoring }));
          return router.navigateByUrl(path);
        },
        navigateToLogin: () => {
          this.loginService.redirectToLogin({ shouldLogoutFirst: !!snapshot(this.store$.pipe(profileOrNot())) });
        },
        navigateToJoin: () => {
          this.loginService.redirectToJoin({ shouldLogoutFirst: !!snapshot(this.store$.pipe(profileOrNot())) });
        },
      },
      gtmService: {
        log: (event: GtmEvent, eventData: GtmEventData) =>
          gtmService.pushEventToDataLayer(event as CommonGtmEvent, eventData as CommonGtmEventData),
        trackElement: (eventData: GtmTrackElementData) =>
          gtmService.trackElement(
            eventData.elementName,
            eventData.context,
            eventData.elementType as ElementTypes,
            eventData.state,
            ''
          ),
      },
      recommendationService: {
        sendFlightSearchEvent: (
          departureDate: string,
          returnDate?: string,
          departureAirport?: string,
          arrivalAirport?: string,
          departureCity?: string,
          arrivalCity?: string,
          paxAdults?: number,
          paxChildren?: number
        ) => {
          this.subscription.add(
            recommendationService
              .sendFlightSearchEvent(
                departureDate,
                returnDate,
                departureAirport,
                arrivalAirport,
                departureCity,
                arrivalCity,
                paxAdults,
                paxChildren
              )
              .subscribe()
          );
        },
      },
      getLoginToken: () => snapshot(this.store$.pipe(loginToken())),
    };
  }

  ngOnInit(): void {
    this.travelDates$ = this.store$.pipe(
      globalBookingDates(),
      map(
        ({ departureDate, returnDate }: GlobalBookingTripDates): TravelDates => ({
          departureDate: departureDate?.toString(),
          returnDate: returnDate?.toString(),
        })
      ),
      finShare()
    );
    this.tripType$ = this.store$.pipe(
      globalBookingTripType(),
      map((tripType: TripType): TravelType => getTravelTypeFromTripType(tripType)),
      finShare()
    );
    this.travelClass$ = this.store$.pipe(
      globalBookingTravelClass(),
      map(
        (travelClass: GlobalBookingTravelClass): TravelClass =>
          travelClass === GlobalBookingTravelClass.BUSINESS ? TravelClass.BUSINESS : TravelClass.MIXED
      ),
      finShare()
    );
    this.flow$ = combineLatest([this.locations$, this.travelDates$, this.tripType$, this.travelClass$]).pipe(
      map(
        ([bookingLocations, travelDates, travelType, travelClass]: [
          BookingLocationCodes[],
          TravelDates,
          TravelType,
          TravelClass,
        ]) => {
          const locations: DeepPartial<Locations[]> = bookingLocations.map((ond) => ({
            origin: ond.originLocationCode ? { locationCode: ond.originLocationCode } : undefined,
            destination: ond.destinationLocationCode ? { locationCode: ond.destinationLocationCode } : undefined,
          }));

          return {
            locations,
            travelDates,
            travelType,
            travelClass,
          };
        }
      ),
      distinctUntilChanged()
    );

    this.localizations$ = this.languageService.localizations.pipe(
      map((localizations: unknown) => parseLocalizations(localizations), distinctUntilChanged())
    );

    this.enablePersonalization$ = this.consentService
      .getCookieConsentStatusById(ConsentTextId.COOKIE_PERSONALIZATION)
      .pipe(distinctUntilChanged());

    if (isPlatformServer(this.platform)) {
      combineLatest([this.flow$, this.localizations$, this.campaignImage$])
        .pipe(take(1))
        .subscribe(
          ([flow, localizations, campaignImage]: [DeepPartial<SelectionState>, Localizations, CmsImageData]) => {
            this.widgetPlaceholder = renderToString({
              env: this.envConfig,
              embedded: this.embedded,
              renderMainHeading: this.shouldRenderMainHeading,
              locale: this.languageService.localeValue,
              enablePersistence: this.enablePersistence,
              localizations,
              flow,
              enableAward: this.enableAward,
              enableAwardIfLoggedIn: this.enableAwardIfLoggedIn,
              enableAurinkomatkat: this.enableAurinkomatkat,
              enablePromoCode: this.enablePromoCode,
              enableCompactWidget: this.enableCompactWidget,
              activatedTab: this.readThenDeleteInitialTabFromUrl(),
              campaignImage: { ...campaignImage, responsiveImageData: {} },
              enableAutoFocus: this.enableAutoFocus,
              isInitialRender: true,
              shouldExpandWidget: false,
              enableOpenJaw: this.enableOpenJaw,
              contentType: this.contentType,
            } as Parameters);
          }
        );
    } else {
      this.widgetPlaceholder = '';
    }
  }

  ngAfterViewInit(): void {
    if (isPlatformServer(this.platform)) {
      return;
    }

    // Render BookingWidget inside this Component
    const mountPoint: HTMLElement = this.widgetRoot.nativeElement;

    const staticParams: Partial<Parameters> = {
      env: this.envConfig,
      extensionApi: this.extensionApi,
      embedded: this.embedded,
      renderMainHeading: this.shouldRenderMainHeading,
      enablePersistence: this.enablePersistence,
    };

    const profile$ =
      this.enableAward || this.enableAwardIfLoggedIn
        ? this.store$.pipe(
            profile(),
            map((profile) => ({
              isLoggedIn: true,
              memberNumber: profile.memberNumber,
              awardPoints: profile.awardPoints,
              tier: profile.tier,
            })),
            startWith({ isLoggedIn: false })
          )
        : of({ isLoggedIn: false });

    this.subscription.add(
      combineLatest([
        this.flow$,
        this.localizations$,
        this.enablePersonalization$.pipe(startWith(undefined)),
        profile$,
        this.campaignImage$,
        this.testVariant$,
        this.countryShopService.getIsEnabled(),
      ]).subscribe(
        ([
          flow,
          localizations,
          enablePersonalization,
          memberProfile,
          campaignImage,
          testVariant,
          isCountryShopTestGroup,
        ]) => {
          renderToElement(mountPoint, {
            ...staticParams,
            locale: this.languageService.localeValue,
            flow: { ...flow, isCountryShopTestGroup },
            localizations,
            enablePersonalization,
            enableAward: this.enableAward,
            enableAwardIfLoggedIn: this.enableAwardIfLoggedIn,
            enableAurinkomatkat: this.enableAurinkomatkat,
            enablePromoCode: this.enablePromoCode,
            enableCompactWidget: this.enableCompactWidget,
            activatedTab: this.readThenDeleteInitialTabFromUrl(),
            profile: memberProfile,
            campaignImage: { ...campaignImage, responsiveImageData: {} },
            enableAutoFocus: this.enableAutoFocus,
            testVariant: testVariant?.variant === TestVariant.B ? WidgetTestVariant.B : WidgetTestVariant.A,
            isInitialRender: this.isInitialRender,
            shouldExpandWidget: this.shouldExpandWidget,
            enableOpenJaw: this.enableOpenJaw,
            contentType: this.contentType,
          } as Parameters);
        }
      )
    );
  }

  ngOnDestroy(): void {
    this.subscription = unsubscribe(this.subscription);
    if (isPlatformBrowser(this.platform)) {
      const mountPoint: HTMLElement = this.widgetRoot.nativeElement;
      unmount(mountPoint);
    }
  }

  /**
   * Reads the queryParam `tab` that will be used to set the initial tab in the booking widget.
   * The queryParam is then deleted from the url.
   */
  readThenDeleteInitialTabFromUrl(): WidgetTab {
    if (this.initialTab) {
      return this.initialTab;
    }

    this.initialTab =
      WidgetTab[this.router.parseUrl(this.router.url).queryParams.tab?.toUpperCase()] ?? WidgetTab.FLIGHT;

    this.updateQueryParamsInUrl({ tab: null });

    return this.initialTab;
  }

  updateQueryParamsInUrl(newParams: Record<string, string>): void {
    this.router.navigate([], {
      queryParams: newParams,
      queryParamsHandling: 'merge',
    });
  }
}
